import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, Route, Routes } from "react-router-dom";
import { czyRolaKonta, type RolaKonta } from "@shared/kontaTypy.js";
import { AppShell } from "./components/shell/AppShell.js";
import { pozycjeDlaRoli } from "./components/shell/pozycjeMenu.js";
import { StanBledu, StanLadowania, StanPusty } from "./components/ui/stany.js";
import { KontoProvider } from "./hooks/useKonto.js";
import { api, ApiError } from "./lib/api.js";
import { DeveloperPage } from "./pages/DeveloperPage.js";
import { KosztyPage } from "./pages/KosztyPage.js";
import { LoginPage } from "./pages/LoginPage.js";
import { MicPage } from "./pages/MicPage.js";
import { RecenzjePage } from "./pages/RecenzjePage.js";
import { SessionPage } from "./pages/SessionPage.js";
import { SessionsPage } from "./pages/SessionsPage.js";
import { SettingsPage } from "./pages/SettingsPage.js";
import { CwiczeniaPage } from "./pages/uzytkownik/CwiczeniaPage.js";
import { CwiczenieSesjaPage } from "./pages/uzytkownik/CwiczenieSesjaPage.js";
import { NowaRozmowaPage } from "./pages/uzytkownik/NowaRozmowaPage.js";
import { StartPage } from "./pages/uzytkownik/StartPage.js";
import { WynikPage } from "./pages/uzytkownik/WynikPage.js";
import { WywolaniePage } from "./pages/WywolaniePage.js";

/**
 * Bramka autoryzacji ma CZTERY stany, nie trzy (P-6).
 *
 * Czwarty ("error") powstal, bo wczesniej blad inny niz 401 z /api/auth/me byl
 * rzucany dalej wewnatrz useEffect. Nikt go nie lapal (nie ma ErrorBoundary),
 * wiec aplikacja zostawala na "checking" NA ZAWSZE, bez zadnego komunikatu -
 * przy padnietym backendzie klient widzial wieczny spinner.
 */
type AuthState =
  | { kind: "checking" }
  | { kind: "anonymous" }
  | { kind: "signed-in"; username: string; role: RolaKonta }
  | { kind: "error"; kod: string };

export function App(): React.JSX.Element {
  const [auth, setAuth] = useState<AuthState>({ kind: "checking" });

  const refresh = useCallback(async () => {
    setAuth({ kind: "checking" });
    try {
      const me = await api.me();
      // Rola spoza kontraktu to konto bez uprawnien: brakujace pole nie moze otworzyc menu administratora.
      setAuth({ kind: "signed-in", username: me.username, role: czyRolaKonta(me.role) ? me.role : "user" });
    } catch (err) {
      // 401 to normalny stan niezalogowanego, nie awaria warta pokazywania.
      if (err instanceof ApiError && err.status === 401) {
        setAuth({ kind: "anonymous" });
        return;
      }
      setAuth({ kind: "error", kod: err instanceof ApiError ? `${err.status} ${err.code}` : "brak połączenia z serwerem" });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // S-5: stan bramki tez mieszka w karcie na washu, a nie jako goly tekst.
  if (auth.kind === "checking" || auth.kind === "error") {
    return (
      <main className="app-main app-main--samotna">
        <div className="app-content">
          <div className="card">
            {auth.kind === "checking" ? (
              <StanLadowania tekst="Sprawdzam sesję..." />
            ) : (
              <StanBledu
                tytul="Nie możemy sprawdzić sesji"
                opis="Serwer nie odpowiada. Spróbuj ponownie za chwilę."
                szczegol={auth.kod}
                akcja={
                  <button type="button" className="btn btn--secondary" onClick={() => void refresh()}>
                    Spróbuj ponownie
                  </button>
                }
              />
            )}
          </div>
        </div>
      </main>
    );
  }

  if (auth.kind === "anonymous") {
    return <LoginPage onSignedIn={refresh} />;
  }

  return (
    <KontoProvider username={auth.username} role={auth.role}>
      <AppShell
        username={auth.username}
        pozycje={pozycjeDlaRoli(auth.role)}
        onSignOut={async () => {
          await api.logout();
          setAuth({ kind: "anonymous" });
        }}
      >
        <TrasyWgRoli rola={auth.role} />
      </AppShell>
    </KontoProvider>
  );
}

/**
 * Trasy aplikacji zalezne od roli. Administrator ma komplet. `user` ma swoj widok: "Moje rozmowy" (/), nowa rozmowa
 * (/nowa), wynik rozmowy (/analizy/:id) i cwiczenia (/cwiczenia, /cwiczenia/:id); kazdy inny adres (takze reczne
 * wpisanie /ustawienia) odsyla na "/". To porzadek w interfejsie - twarda odmowe daje serwer.
 */
export function TrasyWgRoli({ rola }: { rola: RolaKonta }): React.JSX.Element {
  if (rola === "user") {
    return (
      <Routes>
        <Route path="/" element={<StartPage />} />
        <Route path="/nowa" element={<NowaRozmowaPage />} />
        <Route path="/analizy/:id" element={<WynikPage />} />
        <Route path="/cwiczenia" element={<CwiczeniaPage />} />
        <Route path="/cwiczenia/:id" element={<CwiczenieSesjaPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<SessionsPage />} />
      <Route path="/analizy/:id" element={<SessionPage />} />
      <Route path="/mikrofon" element={<MicPage />} />
      <Route path="/recenzje" element={<RecenzjePage />} />
      <Route path="/ustawienia" element={<SettingsPage />} />
      <Route path="/koszty" element={<KosztyPage />} />
      <Route path="/developer" element={<DeveloperPage />} />
      <Route path="/developer/:id" element={<WywolaniePage />} />
      {/* S-8: nieznany adres dostaje zaprojektowany stan, a nie pusta powloke. */}
      <Route
        path="*"
        element={
          <>
            <h1 className="page-title">Nie ma takiej strony</h1>
            <p className="page-lead">Adres jest nieaktualny albo zawiera literówkę.</p>
            <div className="card">
              <StanPusty
                tytul="Ta strona nie istnieje"
                opis="Sprawdź adres albo wróć do listy analiz."
                akcja={
                  <Link className="btn btn--secondary" to="/">
                    Wróć do analiz
                  </Link>
                }
              />
            </div>
          </>
        }
      />
    </Routes>
  );
}
